import { useCallback, useEffect, useState } from 'react';
import { usePreferences } from './usePreferences';
import { useSavePreference } from './useSavePreference';
/**
 * Custom hook similar to useState that persists the preference value in the backend.
 *
 * @template T - The type of the preference value.
 * @param {string} key - The key of the preference.
 * @param {T} defaultValue - The default value of the preference.
 * @param {boolean} skip - Whether to skip the loading state.
 * @returns {[T, (value?: T) => Promise<void>]} - A tuple containing the current value and a function to set the value.
 */
export const usePreferenceState = (key, defaultValue, skip = false) => {
    const { find, loading } = usePreferences(skip);
    const { savePreference } = useSavePreference();
    const preference = find(key);
    const getValue = useCallback(() => {
        if (preference?.value) {
            return JSON.parse(preference.value);
        }
        return defaultValue;
    }, [preference, defaultValue]);
    const [storedValue, setStoredValue] = useState(getValue);
    useEffect(() => {
        let unmounting = false;
        setTimeout(() => {
            const newValue = getValue();
            if (!unmounting && JSON.stringify(storedValue) !== JSON.stringify(newValue)) {
                setStoredValue(newValue);
            }
        }, 0);
        return () => {
            unmounting = true;
        };
    }, [storedValue, getValue]);
    const setValue = async (value) => {
        setStoredValue(value ?? defaultValue);
        try {
            await savePreference(key, JSON.stringify(value ?? defaultValue));
        }
        catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            console.error(e.message);
        }
    };
    return [storedValue, setValue, { loading }];
};
